// ============================================
// moldClaw Pricing Page
// ============================================

/**
 * 🧑‍💻 SWE-1: 가격 페이지 UI
 * 🧑‍💻 SWE-4: Stripe Checkout 연동
 * 🎨 CX-1: UX 리뷰 적용
 * ✍️ CX-2: 카피라이팅
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLicense, TIER_INFO } from '../hooks/useLicense';

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentLicense, checkout } = useLicense();
  const [loading, setLoading] = useState<'pro' | 'team' | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const handleCheckout = async (tier: 'pro' | 'team') => {
    setLoading(tier);
    setError(null);
    
    try {
      await checkout(tier);
    } catch (err) {
      setError(err instanceof Error ? err.message : '결제 처리 중 오류가 발생했습니다');
      setLoading(null);
    }
  };
  
  const isCurrentTier = (tier: string) => currentLicense?.tier === tier;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            심플하고 투명한 가격
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            필요에 맞는 플랜을 선택하세요. 언제든 업그레이드하거나 취소할 수 있습니다.
          </p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-center">
            {error}
          </div>
        )}
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Free Tier */}
          <PricingCard
            tier={TIER_INFO.free}
            isCurrent={isCurrentTier('free')}
            onSelect={() => navigate('/register')}
            disabled={!!user}
            buttonText={user ? '현재 플랜' : TIER_INFO.free.cta}
          />
          
          {/* Pro Tier */}
          <PricingCard
            tier={TIER_INFO.pro}
            isCurrent={isCurrentTier('pro')}
            onSelect={() => handleCheckout('pro')}
            loading={loading === 'pro'}
            disabled={isCurrentTier('pro') || isCurrentTier('team')}
            buttonText={
              isCurrentTier('pro') ? '현재 플랜' :
              isCurrentTier('team') ? '낮은 플랜' :
              TIER_INFO.pro.cta
            }
          />
          
          {/* Team Tier */}
          <PricingCard
            tier={TIER_INFO.team}
            isCurrent={isCurrentTier('team')}
            onSelect={() => handleCheckout('team')}
            loading={loading === 'team'}
            disabled={isCurrentTier('team')}
            buttonText={isCurrentTier('team') ? '현재 플랜' : TIER_INFO.team.cta}
          />
        </div>
        
        {/* FAQ / Additional Info */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            자주 묻는 질문
          </h2>
          
          <div className="space-y-6">
            <FaqItem
              question="언제든 플랜을 변경할 수 있나요?"
              answer="네, 언제든지 업그레이드하거나 다운그레이드할 수 있습니다. 업그레이드 시 즉시 적용되며, 다운그레이드는 다음 결제 주기부터 적용됩니다."
            />
            <FaqItem
              question="환불 정책은 어떻게 되나요?"
              answer="구독 후 7일 이내 사용량이 적은 경우 전액 환불해 드립니다. 고객 지원팀에 문의해 주세요."
            />
            <FaqItem
              question="기기 수 제한은 어떻게 작동하나요?"
              answer="플랜에 따라 동시에 등록할 수 있는 기기 수가 다릅니다. 기기를 교체하고 싶으면 대시보드에서 기존 기기를 삭제한 후 새 기기를 등록하면 됩니다."
            />
            <FaqItem
              question="연간 결제 할인이 있나요?"
              answer="곧 연간 결제 옵션을 제공할 예정입니다. 연간 결제 시 2개월 무료 혜택이 적용됩니다."
            />
          </div>
        </div>
        
        {/* CTA */}
        <div className="mt-20 text-center">
          <p className="text-gray-400 mb-4">
            아직 결정하기 어려우신가요?
          </p>
          <button
            onClick={() => navigate('/register')}
            className="text-space-blue-light hover:text-space-blue-light font-medium transition-colors"
          >
            무료로 시작하기 →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Pricing Card Component
// ============================================

interface PricingCardProps {
  tier: typeof TIER_INFO['free'] | typeof TIER_INFO['pro'] | typeof TIER_INFO['team'];
  isCurrent: boolean;
  onSelect: () => void;
  loading?: boolean;
  disabled?: boolean;
  buttonText: string;
}

function PricingCard({ tier, isCurrent, onSelect, loading, disabled, buttonText }: PricingCardProps) {
  return (
    <div
      className={`relative rounded-2xl p-8 ${
        tier.popular
          ? 'bg-space-blue ring-4 ring-space-blue ring-opacity-50'
          : 'bg-gray-800'
      } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
    >
      {/* Popular Badge */}
      {tier.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-space-blue text-white text-sm font-semibold px-4 py-1 rounded-full">
            인기
          </span>
        </div>
      )}
      
      {/* Current Badge */}
      {isCurrent && (
        <div className="absolute -top-4 right-4">
          <span className="bg-green-500 text-white text-sm font-semibold px-3 py-1 rounded-full">
            현재
          </span>
        </div>
      )}
      
      {/* Tier Name */}
      <h3 className={`text-xl font-bold ${tier.popular ? 'text-white' : 'text-gray-200'}`}>
        {tier.name}
      </h3>
      
      {/* Description */}
      <p className={`mt-2 text-sm ${tier.popular ? 'text-space-blue-light' : 'text-gray-400'}`}>
        {tier.description}
      </p>
      
      {/* Price */}
      <div className="mt-6 flex items-baseline">
        <span className={`text-4xl font-bold ${tier.popular ? 'text-white' : 'text-gray-100'}`}>
          {tier.priceLabel}
        </span>
        {tier.period && (
          <span className={`ml-1 text-lg ${tier.popular ? 'text-space-blue-light' : 'text-gray-500'}`}>
            {tier.period}
          </span>
        )}
      </div>
      
      {/* Features */}
      <ul className="mt-8 space-y-3">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon className={`h-5 w-5 flex-shrink-0 ${tier.popular ? 'text-space-blue-light' : 'text-green-500'}`} />
            <span className={`ml-3 text-sm ${tier.popular ? 'text-white' : 'text-gray-300'}`}>
              {feature}
            </span>
          </li>
        ))}
        {tier.limitations.map((limitation, index) => (
          <li key={`limit-${index}`} className="flex items-start">
            <XIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
            <span className="ml-3 text-sm text-gray-500">
              {limitation}
            </span>
          </li>
        ))}
      </ul>
      
      {/* CTA Button */}
      <button
        onClick={onSelect}
        disabled={disabled || loading}
        className={`mt-8 w-full py-3 px-4 rounded-lg font-semibold transition-all ${
          tier.popular
            ? 'bg-white text-space-blue hover:bg-gray-100 disabled:bg-gray-300'
            : 'bg-space-blue text-white hover:bg-space-blue hover:brightness-110 disabled:bg-gray-600'
        } disabled:cursor-not-allowed`}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <LoadingSpinner />
            처리 중...
          </span>
        ) : (
          buttonText
        )}
      </button>
    </div>
  );
}

// ============================================
// FAQ Item Component
// ============================================

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-2">{question}</h3>
      <p className="text-gray-400">{answer}</p>
    </div>
  );
}

// ============================================
// Icons
// ============================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
